import React, {PureComponent} from 'react';
import I18n from 'app/i18n';
import {isGuest} from 'app/utils';
import {isNewDesktop} from 'app/utils/windowFunctions';
import {size380x380} from 'app/utils/CasinoGameImages';
import {SignInButton} from 'app/components/auth/SignInButton';
import {
    captureCasinoGameLaunchRow,
    favoriteGameChange,
    startCasino,
    startDemo,
    startGame
} from 'app/services/CasinoServices';
import {showCasinoSignUp} from 'app/utils/IsRegisterUser';
import {CasinoFilterInterface} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import {goToCasino} from 'app/utils/app_links';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {showRestrictedCountriesPopup} from 'app/components/pages/casino/RestrictedCountriesPopup';

interface CasinoGameItemProps {
    id: number,
    name: string,
    identifier: string,
    identifier2: string,
    provider_name: string,
    favorite: boolean,
    payout?: number,
    filter?: CasinoFilterInterface,
    setActiveGame: (id: number) => void,
    deactivateGame: () => void
    points: boolean
    mainPage?: boolean
    cool_off_present?: boolean
    useStartCasino?: boolean
    released_at?: number | null
    restricted?: boolean
    restricted_country_codes?: string[]
    in_out?: boolean
    image_url?: string | null
    active?: boolean
}

export default class CasinoGameItem extends PureComponent<CasinoGameItemProps> {
    rootElement = React.createRef<HTMLDivElement>();

    openRestrictedPopup = (): void => {
        const {name, restricted_country_codes} = this.props;

        showRestrictedCountriesPopup(name, restricted_country_codes);
    };

    componentDidUpdate(): void {
        if (this.props.active) {
            document.addEventListener('click', this.handleClickOutside, true);
        } else {
            document.removeEventListener('click', this.handleClickOutside);
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.handleClickOutside);
    }

    handleClickOutside = (e: MouseEvent): void => {
        if (this.props.active && this.rootElement.current &&
            !this.rootElement.current.contains(e.target as HTMLElement)) {
            this.props.deactivateGame();
        }
    };

    favoriteGameChoice = (id: number, favorite: boolean, filterId: number): void => {
        favoriteGameChange(id, favorite, filterId);
    };

    handleChooseFavoriteGame = (): void => {
        const {id, favorite, filter} = this.props;

        if (showCasinoSignUp()) {
            return;
        }
        this.favoriteGameChoice(id, !favorite, filter && filter.id);
    };

    setActiveGame = (): void => {
        const {id, setActiveGame} = this.props;

        setActiveGame?.(id);
    };

    handleStartDemo = (): void => {
        this.prepareGameLaunch();
        this.launchCasinoGame(true);
    };

    handleStartGame = (): void => {
        this.prepareGameLaunch();
        this.launchCasinoGame();
    };

    prepareGameLaunch = (): void => {
        this.setActiveGame();
        captureCasinoGameLaunchRow(this.rootElement.current);
    };

    launchCasinoGame = (demo = false): void => {
        const {id, filter, mainPage, useStartCasino} = this.props;

        if (mainPage) {
            goToCasino();
            startCasino(id, null, demo);
            return;
        }

        if (useStartCasino) {
            startCasino(id, null, demo);
        } else if (demo) {
            startDemo(id, filter);
        } else {
            startGame(id, filter);
        }
    };

    buildRootClassName = (): string => {
        const {active, restricted} = this.props;

        return [
            'casino-game-item',
            restricted && 'casino-game-item--restricted',
            active && 'casino-game-item--launched'
        ].filter(Boolean).join(' ');
    };

    render(): React.ReactNode {
        const {
            identifier, identifier2, provider_name, favorite, points, name, payout,
            cool_off_present, restricted, released_at, in_out, image_url
        } = this.props;
        return <div
            ref={this.rootElement}
            data-casino-game-id={this.props.id}
            className={this.buildRootClassName()}
        >
            <div className="casino-game-image">
                <img
                    alt={identifier2}
                    src={size380x380(provider_name, identifier, released_at, in_out, image_url)}/>
            </div>
            {restricted && <div className="casino-game-restricted-overlay">
                <SpriteIcon name="#svg-traffic-barrier" className="svg-default casino-game-restricted-overlay__icon"/>
                <div className="casino-game-restricted-overlay__container">
                    <SpriteIcon name="#svg-traffic-barrier" className="svg-default casino-game-restricted-overlay__container-icon"/>
                    <span className="casino-game-restricted-overlay__text">
                        {I18n.t('casino_slot_restricted_text')}
                    </span>
                    <button
                        className="casino-game-restricted-overlay__details"
                        onClick={this.openRestrictedPopup}
                    >
                        {I18n.t('casino_slot_restricted_details')}
                    </button>
                </div>
            </div>}
            <div
                className={`casino-game-name-container${this.props.active ? ' active' : ''}`}>
                <span className="casino-game-name">{name}</span>
                {payout && <span className="casino-game-rtp">{`${payout}%`}</span>}
                {points && <div className="casino-game-points">
                    <span className="casino-game-points__label">{`+ ${I18n.t('user.points')}`}</span>
                </div>}
            </div>
            <button
                className={`button-casino-like ${favorite ? 'active' : ''}`}
                onClick={this.handleChooseFavoriteGame}>
                <span className="button-casino-like__icon">
                    <SpriteIcon name="#svg-button-favourite" className="svg-default"/>
                </span>
            </button>
            {!isNewDesktop() && <button
                className="button-show-buttons"
                onClick={this.setActiveGame}>
            </button>}
            <div className={`casino-game-buttons${this.props.active ? ' active' : ''}`}>
                <div className="casino-game-buttons__inner">
                    {isGuest()
                        ? <>
                            <SignInButton title={I18n.t('play')} usingOnTab={'CASINO'}/>
                            <SignInButton
                                usingOnTab={'CASINO'}
                                className={'button-adaptive_casino-icon'}
                            >
                                <SpriteIcon name="#svg-button-eye" className="svg-default"/>
                                <span className="button-adaptive__name">
                                    {I18n.t('demo')}
                                </span>
                            </SignInButton>
                        </>
                        : <>
                            <button
                                disabled={cool_off_present}
                                className="button-adaptive button-adaptive_casino"
                                onClick={this.handleStartGame}>
                                {I18n.t('play')}
                            </button>
                            <button
                                disabled={cool_off_present}
                                className="button-adaptive_casino-icon"
                                onClick={this.handleStartDemo}
                            >
                                <SpriteIcon name="#svg-button-eye" className="svg-default"/>
                                <span className="button-adaptive__name">
                                    {I18n.t('demo')}
                                </span>
                            </button>
                        </>}
                </div>
            </div>
        </div>;
    }
}
